import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { BRAND, MEDIA } from "../lib/constants";
import { SERVICES, COMMON_PROBLEMS, WHO_WE_SERVE } from "../data/services";
import { BLOG_POSTS } from "../data/blogPosts";
import CTABand from "../components/CTABand";
import ShieldMotif from "../components/ShieldMotif";
import { useSEO } from "../lib/seo";

export default function Home() {
  useSEO({
    title: "Healthcare Operations & Compliance Consulting · St. Louis, MO",
    description:
      "AthenaCrest Defense helps Missouri skilled nursing facilities and long-term care operators reduce compliance risk, recover Medicaid revenue, and stabilize operations. Based in St. Louis, serving Missouri statewide.",
    path: "/",
  });
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-ivory pt-12 pb-24 sm:pt-20 sm:pb-32">
        {/* Background imagery */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${MEDIA.heroBg})`,
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/95 to-ivory/40" />
        <div className="absolute inset-0 hero-grid pointer-events-none" />

        {/* Watermark logo */}
        <img
          src={BRAND.logoUrl}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="absolute -right-32 top-1/2 -translate-y-1/2 w-[680px] opacity-[0.06] pointer-events-none"
        />

        <div className="container-luxe relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8 animate-fade-up">
              <div className="flex items-center gap-4 mb-8">
                <span className="gold-line" />
                <span className="overline">
                  Healthcare Operations &amp; Compliance Advisory
                </span>
              </div>

              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-navy mb-8">
                Protect the Facility.<br />
                Restore Compliance.<br />
                <span className="italic text-teal">Recover Revenue.</span>
              </h1>

              <p className="font-sans text-lg sm:text-xl text-navy/75 max-w-2xl leading-relaxed mb-10">
                AthenaCrest Defense is a St. Louis, Missouri based healthcare
                consulting firm. We help skilled nursing facilities and
                long-term care organizations across Missouri identify
                operational failures, reduce compliance risk, improve Medicaid
                workflow efficiency, and strengthen facility performance before
                problems turn into citations, denials, lawsuits, or revenue
                loss.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={BRAND.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary group"
                  data-testid="hero-cta-primary"
                >
                  Book a Confidential Consultation
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/services"
                  className="btn-ghost"
                  data-testid="hero-cta-secondary"
                >
                  Explore Services
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 hidden lg:block relative">
              <div className="relative aspect-[3/4] w-full">
                <ShieldMotif
                  className="absolute inset-0 w-full h-full"
                  opacity={0.18}
                />
                <div className="absolute inset-12 border border-gold/40 flex items-center justify-center">
                  <img
                    src={BRAND.logoUrl}
                    alt="AthenaCrest Defense"
                    loading="lazy"
                    decoding="async"
                    className="w-full p-6 animate-fade-in"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Authority strip */}
          <div className="mt-20 pt-10 border-t border-champagne-200 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { k: "100%", v: "Confidential Engagements" },
              { k: "SNF + LTC", v: "Operational Focus" },
              { k: "Missouri", v: "St. Louis Based · Statewide" },
              { k: "MJ", v: "Health Law Trained" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-serif text-3xl text-navy">{s.k}</div>
                <div className="font-sans text-xs uppercase tracking-overline text-gold mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON PROBLEMS */}
      <section className="bg-paper section-pad" data-testid="problems-section">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-5">What We Fix</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                Operational problems that quietly cost facilities{" "}
                <span className="italic text-teal">millions.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="font-sans text-lg text-navy/70 leading-relaxed">
                These are the pressure points we walk into every week. Each
                one signals revenue loss, regulatory exposure, or operational
                drift, and usually all three. We diagnose the root cause and
                install the workflow that resolves it.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-champagne-200 border border-champagne-200">
            {COMMON_PROBLEMS.map((p, i) => (
              <div
                key={p}
                className="bg-ivory p-8 hover:bg-champagne-100 transition-colors duration-300 group"
                data-testid={`problem-card-${i}`}
              >
                <div className="font-serif text-gold text-2xl mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-sans text-navy text-base leading-relaxed group-hover:text-teal transition-colors">
                  {p}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ATHENACREST */}
      <section
        className="section-pad bg-ivory relative overflow-hidden"
        data-testid="why-section"
      >
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1 relative">
              <div className="aspect-[4/5] relative">
                <img
                  src={MEDIA.corporateOffice}
                  alt="Executive operational strategy"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute -bottom-8 -right-8 w-48 h-48 border border-gold hidden sm:block" />
                <div className="absolute -top-6 -left-6 bg-navy text-ivory px-6 py-4 hidden sm:block">
                  <div className="font-serif text-3xl text-gold">15+</div>
                  <div className="text-xs uppercase tracking-overline">
                    Years inside SNFs
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="overline mb-5">Why AthenaCrest Defense</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05] mb-8">
                We do not theorize.<br />
                <span className="italic">We have run the floor.</span>
              </h2>
              <div className="space-y-5">
                {[
                  "Real SNF operational experience inside facilities under pressure.",
                  "Healthcare compliance expertise across federal and state regulatory frameworks.",
                  "Legal and regulatory literacy from MJ level health law training.",
                  "Strategic consulting calibrated to ownership and corporate priorities.",
                  "Practical, installable solutions. Not slide decks the team will never use.",
                  "A working understanding of facility pressure and what stakeholders actually expect.",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-4 group">
                    <span className="mt-2 w-1.5 h-1.5 bg-gold rotate-45 group-hover:rotate-90 transition-transform duration-500 flex-shrink-0" />
                    <p className="font-sans text-base text-navy/80 leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-10 font-sans text-sm uppercase tracking-wide text-navy hover:text-gold transition-colors group"
                data-testid="why-meet-sierra"
              >
                Meet Sierra Thomas
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section
        className="section-pad bg-navy text-ivory relative overflow-hidden"
        data-testid="who-section"
      >
        <ShieldMotif
          className="absolute -right-40 top-1/2 -translate-y-1/2 w-[600px]"
          opacity={0.05}
        />
        <div className="container-luxe relative z-10">
          <div className="max-w-3xl mb-16">
            <div className="overline mb-5">Who We Serve</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05]">
              Built for the operators carrying the{" "}
              <span className="italic text-gold">most pressure.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {WHO_WE_SERVE.map((w, i) => (
              <div
                key={w.title}
                className="border border-ivory/15 p-8 hover:border-gold transition-colors duration-500 group"
                data-testid={`serve-card-${i}`}
              >
                <div className="font-serif text-gold text-xl mb-3">
                  0{i + 1}
                </div>
                <h3 className="font-serif text-2xl text-ivory mb-3 group-hover:text-gold transition-colors">
                  {w.title}
                </h3>
                <p className="font-sans text-sm text-ivory/65 leading-relaxed">
                  {w.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        className="section-pad bg-ivory"
        data-testid="services-preview-section"
      >
        <div className="container-luxe">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="overline mb-5">Services</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
                Eight engagements designed to{" "}
                <span className="italic text-teal">stabilize the building.</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="btn-ghost flex-shrink-0"
              data-testid="services-view-all"
            >
              View All Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-champagne-200 border border-champagne-200">
            {SERVICES.slice(0, 4).map((s) => (
              <Link
                key={s.slug}
                to="/services"
                className="bg-ivory p-10 hover:bg-champagne-100 transition-all duration-500 group block"
                data-testid={`service-preview-${s.slug}`}
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <span className="font-serif text-gold text-3xl">{s.number}</span>
                  <ArrowUpRight className="h-5 w-5 text-navy/40 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-navy mb-4 group-hover:text-teal transition-colors leading-tight">
                  {s.title}
                </h3>
                <p className="font-sans text-base text-navy/65 leading-relaxed">
                  {s.short}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section
        className="section-pad bg-champagne-100"
        data-testid="blog-preview-section"
      >
        <div className="container-luxe">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-5">Knowledge Center</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05]">
                Field notes from inside the building.
              </h2>
            </div>
            <Link
              to="/knowledge-center"
              className="btn-ghost flex-shrink-0"
              data-testid="blog-view-all"
            >
              View All Articles
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.slice(0, 3).map((p) => (
              <Link
                key={p.slug}
                to={`/knowledge-center/${p.slug}`}
                className="luxe-card group flex flex-col"
                data-testid={`blog-preview-${p.slug}`}
              >
                <div className="overline text-[10px] mb-3">{p.category}</div>
                <h3 className="font-serif text-2xl text-navy mb-4 group-hover:text-teal transition-colors leading-tight">
                  {p.title}
                </h3>
                <p className="font-sans text-sm text-navy/65 leading-relaxed mb-6 flex-1">
                  {p.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-champagne-200 text-xs font-sans text-navy/50">
                  <span>{p.date}</span>
                  <span>{p.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand testId="home-cta-band" />
    </div>
  );
}
